import { ProximoPlan } from "@/components/ocio/ProximoPlan";
import React, { useState } from "react";
import { OrganizarFoto } from "@/components/inicio/OrganizarFoto";
import { ahoraMin } from "@/lib/zona";
import { buildArchivo, totalDocsArchivo } from "@/lib/archivo";
import { useOcurrencias } from "@/components/gestiones/Ocurrencias";
import { NecesitaAtencion } from "@/components/inicio/NecesitaAtencion";
import { ProximasFechas } from "@/components/inicio/ProximasFechas";
import { itemsAtencion, itemsProximas } from "@/lib/inicio";
import { api } from "@/lib/api";
import { ZonaAviso } from "@/components/ZonaHoraria";
import { PushBanner } from "@/components/push/PushPrefs";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { STATUS } from "@/mock/data";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { GastosInicio } from "@/components/gastos/GastosInicio";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const greeting = () => {
  const h = Math.floor(ahoraMin() / 60);
  if (h >= 5 && h < 14) return "Buenos días";
  if (h >= 14 && h < 21) return "Buenas tardes";
  return "Buenas noches";
};

export default function Inicio() {
  const navigate = useNavigate();
  const data = useData();
  const { matters } = data;
  const totalArchivo = React.useMemo(() => totalDocsArchivo(buildArchivo(data)), [data]);
  const { user, logout } = useAuth();
  const [confirmLogout, setConfirmLogout] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const attention = matters.filter((m) => m.status === STATUS.ATTENTION);
  const upcoming = matters.filter((m) => m.status === STATUS.UPCOMING);
  const [ocs, recargarOcs] = useOcurrencias();
  const [gestiones, setGestiones] = useState([]);
  React.useEffect(() => {
    const cargar = () => api.get("/gestiones").then((r) => setGestiones(r.data)).catch(() => {});
    cargar();
    window.addEventListener("aldia-gestiones", cargar);
    return () => window.removeEventListener("aldia-gestiones", cargar);
  }, []);
  const atencion = itemsAtencion({ matters, reminders: data.reminders, familyMembers: data.familyMembers });
  const proximas = itemsProximas({ matters, reminders: data.reminders, gestiones, familyMembers: data.familyMembers, excluir: new Set(atencion.map((i) => i.id)) });
  const nAtencion = atencion.length + ocs.length;

  return (
    <div className="space-y-6">
      <ZonaAviso />
      {/* Saludo + resumen */}
      <header className="aldia-fade-up">
        <div className="flex items-center justify-between">
          <p className="font-heading text-sm font-bold uppercase tracking-widest text-[#3A6B56]">
            AL DÍA
          </p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="perfil-link"
                aria-label="Tu perfil y opciones"
                className="w-11 h-11 rounded-full bg-[#3A6B56] flex items-center justify-center font-heading font-bold text-lg text-white hover:bg-[#2E5645] transition-colors focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/20"
              >
                {(user?.nombre || "?").charAt(0)}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-white border-[#E5DFD5] min-w-[180px]" data-testid="avatar-menu">
              <DropdownMenuItem
                data-testid="avatar-menu-perfil"
                onClick={() => navigate("/perfil")}
                className="text-base text-[#1C2421] cursor-pointer gap-2 py-2.5"
              >
                <Icons.User size={18} className="text-[#3A6B56]" /> Tu perfil
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#E5DFD5]" />
              <DropdownMenuItem
                data-testid="avatar-menu-logout"
                onClick={() => setConfirmLogout(true)}
                className="text-base text-[#B91C1C] cursor-pointer gap-2 py-2.5 focus:bg-[#FEF2F2] focus:text-[#B91C1C]"
              >
                <Icons.LogOut size={18} /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog open={confirmLogout} onOpenChange={setConfirmLogout}>
            <AlertDialogContent data-testid="logout-confirm" className="bg-[#FAF8F5] border-[#E5DFD5]">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading text-xl text-[#1C2421]">¿Cerrar sesión?</AlertDialogTitle>
                <AlertDialogDescription className="text-base text-[#6E7A74]">
                  Tendrás que volver a iniciar sesión para acceder a tus asuntos.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2">
                <AlertDialogCancel data-testid="logout-cancel" className="min-h-[52px] text-base">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="logout-confirm-btn"
                  onClick={doLogout}
                  className="min-h-[52px] text-base bg-[#B91C1C] hover:bg-[#991B1B] text-white"
                >
                  Cerrar sesión
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421] mt-1 leading-tight">
          {greeting()}, {user?.nombre}.
        </h1>
        <div className="mt-4 bg-white border border-[#E5DFD5] rounded-2xl p-5 shadow-sm flex items-start gap-4">
          <div className="shrink-0 w-12 h-12 rounded-full bg-[#E8F0EC] flex items-center justify-center">
            <Icons.ShieldCheck size={26} className="text-[#3A6B56]" />
          </div>
          <p className="text-lg sm:text-xl text-[#1C2421] leading-relaxed font-medium">
            {nAtencion > 0 ? (
              <>
                Tienes{" "}
                <strong className="text-[#B91C1C]">
                  {nAtencion} {nAtencion === 1 ? "cosa" : "cosas"}
                </strong>{" "}
                {nAtencion === 1 ? "que necesita" : "que necesitan"} tu atención.
              </>
            ) : (
              <span data-testid="home-nada-pendiente">No tienes nada que requiera tu atención ahora mismo.</span>
            )}
          </p>
        </div>
      </header>
      <OrganizarFoto />
      <PushBanner />

      {/* Contadores: resumen y filtros directos */}
      <div className="grid grid-cols-3 gap-3" data-testid="summary-counters">
        <Counter testid="counter-attention" color="#DC2626" bg="#FEF2F2" value={attention.length} label="Requiere atención"
          onClick={() => navigate("/asuntos?estado=attention")} />
        <Counter testid="counter-upcoming" color="#D97706" bg="#FFFBEB" value={upcoming.length} label="Asuntos próximos"
          onClick={() => navigate("/asuntos?estado=upcoming")} />
        <Counter testid="counter-controlled" color="#16A34A" bg="#F0FDF4" value={totalArchivo} label="Al día"
          onClick={() => navigate("/archivo")} />
      </div>

      <button data-testid="home-mis-areas" onClick={() => navigate("/areas")}
        className="w-full min-h-[72px] rounded-2xl bg-white border-2 border-[#3A6B56] px-5 py-3 flex items-center gap-4 text-left shadow-sm hover:bg-[#F0F6F2] transition-colors">
        <span className="w-12 h-12 rounded-xl bg-[#E8F0EC] flex items-center justify-center shrink-0"><Icons.LayoutGrid size={26} className="text-[#3A6B56]" /></span>
        <span className="flex-1 min-w-0">
          <span className="block font-heading font-bold text-xl text-[#1C2421]">Mis áreas</span>
          <span className="block text-base text-[#4A5550]">Viviendas, vehículos, personas, mascotas, gastos, ocio y más</span>
        </span>
        <Icons.ChevronRight size={24} className="text-[#3A6B56] shrink-0" />
      </button>

      <NecesitaAtencion items={atencion} ocs={ocs} recargarOcs={recargarOcs} />

      <ProximoPlan />

      <ProximasFechas items={proximas} />

      <GastosInicio />

      <div className="space-y-3 pt-2">
        <button data-testid="home-ver-areas" onClick={() => navigate("/areas")}
          className="w-full min-h-[64px] rounded-2xl bg-[#3A6B56] text-white font-bold text-xl inline-flex items-center justify-center gap-2 shadow-md hover:bg-[#2E5645]">
          <Icons.LayoutGrid size={24} /> Ver todas mis áreas
        </button>
        <p className="text-base text-[#4A5550] text-center">Consulta viviendas, vehículos, ocio y reservas, contratos, personas, mascotas, documentos y recordatorios.</p>
        <button data-testid="home-anadir-info" onClick={() => window.dispatchEvent(new Event("aldia-add"))}
          className="w-full min-h-[56px] rounded-2xl bg-white border-2 border-[#C9D8CF] text-[#2E5645] font-semibold text-lg inline-flex items-center justify-center gap-2 hover:bg-[#F0F6F2]">
          <Icons.PlusCircle size={22} /> Añadir información
        </button>
        <p className="text-base text-[#4A5550] text-center">Añade una foto, un documento, un correo o un recordatorio.</p>
      </div>
    </div>
  );
}

const Counter = ({ color, bg, value, label, onClick, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className="rounded-2xl p-4 text-center border transition-shadow hover:shadow-md focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/15"
    style={{ backgroundColor: bg, borderColor: `${color}33` }}
  >
    <p className="font-heading text-3xl font-extrabold" style={{ color }}>
      {value}
    </p>
    <p className="text-sm font-semibold text-[#4A5550] mt-0.5">{label}</p>
  </button>
);
